import { Avatar, Box, Chip, Divider, Grid, IconButton, InputProps, TextField } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import { cloneDeep } from 'lodash';
import useScreenWidth from '../../../hooks/useScreenWidth';
import { InputInfo } from '../../../models/APIModels';
import clientUtils from '../../../Utils/clientUtils';
import AutoComplete from '../../AutoComplete/AutoCompleteSelect';
import { useForm, useFormContext } from 'react-hook-form';
import InputConfiguration from './InputConfiguration';
import OptionConfiguration from './OptionConfiguration';
import { multilineEnabler } from '../../../Utils/displaySettingsUtils';
import { InterfaceTypes } from '../../../enums';
import CancelIcon from '@mui/icons-material/Cancel';

const addInput = [
  {
    name: 'addInput',
    inputDescription: '',
    inputType: '',
    options: [],
  },
];

interface IProps {
  formValues: any;
  dataTypes: any[];
}
const DefineModelInputsPanel = ({ formValues, dataTypes }: IProps) => {
  // ➡️ Variables .....
  const widthSize = useScreenWidth();
  const [inputs, setInputs] = useState<InputInfo[]>([]);
  const [selectedInput, setSelectedInput] = useState();
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [selectedOption, setSelectedOption] = useState();
  const [showOptionsAsMultiLine, setShowOptionsAsMultiLine] = useState(false);

  const {
    register,
    getValues,
    setValue,
    watch,
    formState: { errors },
  } = useFormContext();

  const watchInterfaceType = watch('interfaceType', 'Form');
  // ➡️ Functions .....

  // ⛏️ Function/Method :: Configure variableDisplayOptions
  const setVal = (name: string, data: any) => {
    formValues.setValue(name, data);
  };
  const configureVariableDisplayOptions = (mode: string, inputDetails: any, setFor?: string) =>
    multilineEnabler(mode, 'inputcon', inputDetails?.name, formValues.getValues('variableDisplayOptions'), setVal, setFor);

  // ⚒️ Function/Method :: for adding new inputs in input configurations .....
  const addNewInput = () => {
    const newInput = {
      name: 'New Input',
      inputDescription: '',
      inputType: 'String',
      options: [],
    };
    let newInputs = cloneDeep(inputs);
    setSelectedInput(newInput);
    setSelectedOption(null);
    newInputs.push(newInput);
    setInputs(newInputs);
  };
  const handleInputClick = (input) => {
    // console.log(input);
    input.inputDescription = input.inputDescription ?? '';
    if (input && input.options) {
      input.options = input.options.map((o) => {
        return {
          ...o,
          optionFriendlyName: o.optionFriendlyName,
        };
      });
    }
    setSelectedInput(input);
    if (input.options.length > 0) {
      const find = input.options.find((o) => o.optionFriendlyName && o.optionFriendlyName.length > 6);
      if (find) {
        setShowOptionsAsMultiLine(true);
      } else {
        setShowOptionsAsMultiLine(false);
      }
      setSelectedOption(input.options[0]);
    } else {
      setSelectedOption();
    }
  };
  const handleDeleteInput = (e, input) => {
    e.preventDefault();
    const newInputs = inputs.filter((i) => i.name !== input.name);
    setInputs(newInputs);
    setValue('aiEngineConfig.inputs', newInputs);
    setSelectedInput(null);
    setSelectedOption(null);
    configureVariableDisplayOptions('pop', input);
  };
  const handleDeleteOption = (e, option) => {
    const toUpdateInput = cloneDeep(selectedInput);
    let options = cloneDeep(selectedInput.options);
    options = options.filter((o) => o.optionFriendlyName !== option.optionFriendlyName);
    toUpdateInput.options = options;
    // console.log('options', options, toUpdateInput);
    setSelectedInput(toUpdateInput);
    setSelectedOption('');
    setSelectedOptions(options);

    const cloneInputs = cloneDeep(inputs);
    const index = cloneInputs.findIndex((i) => i.name === toUpdateInput.name);
    if (index !== -1) {
      cloneInputs[index] = toUpdateInput;
      setInputs(cloneInputs);
      setValue('aiEngineConfig.inputs', cloneInputs);
    }
  };

  const handleOptionClick = (option) => {
    setSelectedOption(option);
  };

  const addNewOption = () => {
    const newOption = {
      optionFriendlyName: 'New Option',
      optionType: 'Data',
      optionValue: '',
    };
    if (selectedInput) {
      const toUpdateInput = cloneDeep(selectedInput);
      let options = cloneDeep(selectedInput.options);
      options = options.filter((o) => o.optionFriendlyName !== '' && o.optionValue !== '');
      options.push(newOption);

      toUpdateInput.options = options;
      setSelectedInput(toUpdateInput);
      setSelectedOption(newOption);
      setSelectedOptions(options);
    }
  };

  // ⛏️ Function/Method :: Updating Input Configuration Values .....
  const updateInputConfiguration = (inputs: InputInfo[], selectedInput: InputInfo) => {
    // console.clear()
    // console.log("updateInputConfiguration::", inputs)
    setInputs(inputs);
    setSelectedInput(selectedInput);
    setValue('aiEngineConfig.inputs', inputs);
  };
  const updateOptionConfiguration = (selectedInput: InputInfo, selectedOption: any) => {
    // setInputs(inputs);
    setSelectedInput(selectedInput);
    setSelectedOption(selectedOption);
    const cloneInputs = cloneDeep(inputs);
    const index = cloneInputs.findIndex((i) => i.name === selectedInput.name);
    if (index !== -1) {
      cloneInputs[index] = selectedInput;
      setInputs(cloneInputs);
      setValue('aiEngineConfig.inputs', cloneInputs);
    }
  };

  // ➡️ Hooks .....

  useEffect(() => {
    let newInputs = formValues.getValues('aiEngineConfig.inputs') ? formValues.getValues('aiEngineConfig.inputs') : [];
    setInputs(newInputs);
    return () => {
      // console.log('cleaned up');
    };
  }, []);

  useEffect(() => {
    if (selectedInput) {
      setSelectedOptions(selectedInput.options);
    }
  }, [selectedInput]);

  useEffect(() => {
    // console.clear()
    // console.log('dataTypes', dataTypes);
  }, [dataTypes]);

  return (
    <Grid container direction="row" justifyContent="space-evenly" spacing={3} sx={{ marginLeft: '0px' }}>
      {/* List of Inputs ..... */}
      <Grid item xs={12} sm={12} md={4} style={clientUtils.getGridStyle('left', widthSize)}>
        <Grid container spacing={2} direction="row">
          <Grid item xs={12}>
            <Box className="hedading">
              <div className="title">List of Inputs</div>
              <div className="line"></div>
            </Box>
          </Grid>

          {/* Added Input List ...... */}
          {inputs.length > 0 &&
            inputs.map((input, index) => {
              return (
                <Grid item xs={6} sm={4} md={6} lg={4} key={inputs.indexOf(input)}>
                  <Chip
                    label={input.name}
                    variant={selectedInput && input.name === selectedInput.name ? 'filled' : 'outlined'}
                    color="success"
                    size="small"
                    // avatar={<Avatar color="success">{input.name.charAt(0)}</Avatar>}
                    onDelete={(e) => handleDeleteInput(e, input)}
                    clickable
                    onClick={(e) => handleInputClick(input)}
                    title={input.name}
                    deleteIcon={
                      <IconButton
                        disabled={
                          (watchInterfaceType === InterfaceTypes.Chatbot && input.inputType !== 'File')
                        }
                      >
                        {(watchInterfaceType === InterfaceTypes.Form ||
                          (watchInterfaceType === InterfaceTypes.Chatbot && input.inputType === 'File')) && (
                          <CancelIcon fontSize="small" />
                        )}
                      </IconButton>
                    }
                  />
                </Grid>
              );
            })}

          {/* Add new Inputs ...... */}
          <Grid item xs={6} sm={4} md={6} lg={4}>
            {watchInterfaceType === InterfaceTypes.Form && (
              <Chip
                label="Add New Input"
                // variant="outlined"
                color="primary"
                size="small"
                clickable
                icon={<AddIcon />}
                onClick={(e) => addNewInput()}
                title="Add New Input"
                disabled={watchInterfaceType === InterfaceTypes.Chatbot}
              />
            )}
          </Grid>
        </Grid>
      </Grid>

      {/* Input Configurations ..... */}
      <Grid item xs={12} sm={12} md={4} style={clientUtils.getGridStyle('right', widthSize)}>
        <InputConfiguration
          selectedInput={selectedInput}
          inputs={inputs}
          updateInputConfiguration={updateInputConfiguration}
          configureVariableDisplayOptions={configureVariableDisplayOptions}
          dataTypes={dataTypes}
          disabled={!selectedInput || watchInterfaceType === InterfaceTypes.Chatbot}
        />
      </Grid>

      {/* Input Options ..... */}
      <Grid item xs={12} sm={12} md={4} style={clientUtils.getGridStyle('right', widthSize)}>
        <Grid container spacing={1} direction="row">
          <Grid item xs={12}>
            <Box className="hedading">
              <div className="title">Input Options</div>
              <div className="line"></div>
            </Box>
          </Grid>
          {selectedOptions &&
            selectedOptions.length > 0 &&
            selectedInput &&
            selectedOptions.map((option, index) => {
              return (
                <Grid
                  item
                  xs={6}
                  sm={4}
                  md={showOptionsAsMultiLine ? 12 : 6}
                  lg={showOptionsAsMultiLine ? 12 : 4}
                  key={option.optionValue}
                >
                  <Chip
                    label={option.optionFriendlyName ? option.optionFriendlyName : option.optionValue}
                    variant={'outlined'}
                    color="success"
                    size="small"
                    variant={
                      selectedOption && selectedOption.optionFriendlyName === option.optionFriendlyName ? 'filled' : 'outlined'
                    }
                    // avatar={<Avatar color="success">{option.name.charAt(0)}</Avatar>}
                    clickable
                    onClick={(e) => handleOptionClick(option)}
                    onDelete={(e) => handleDeleteOption(e, option)}
                    title={option.optionFriendlyName ? option.optionFriendlyName : option.optionValue}
                  />
                </Grid>
              );
            })}
          <Grid item xs={6} sm={4} md={6} lg={4}>
            {watchInterfaceType === InterfaceTypes.Form && (
              <Chip
                label="Add New Option"
                // variant="outlined"
                color="primary"
                size="small"
                clickable
                icon={<AddIcon />}
                onClick={(e) => addNewOption()}
                title="Add New Option"
                disabled={
                  watchInterfaceType === InterfaceTypes.Chatbot ||
                  !selectedInput ||
                  (selectedOption && selectedOption.optionFriendlyName === '' && selectedOption.optionValue === '')
                }
              />
            )}
          </Grid>
        </Grid>
        <OptionConfiguration
          selectedInput={selectedInput}
          selectedOption={selectedOption}
          inputs={inputs}
          updateOptionConfiguration={updateOptionConfiguration}
          disabled={!selectedOption || watchInterfaceType === InterfaceTypes.Chatbot}
        />
      </Grid>
    </Grid>
  );
};

export default DefineModelInputsPanel;
